import { type FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  DashboardErrorState,
  DashboardExportButton,
  DashboardNarrativePanel,
  DashboardSectionHeader,
} from "src/components/common";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import MonthRangePicker from "src/components/reusable/MonthRangePicker/MonthRangePicker";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import {
  DEFAULT_SALES_ATTRIBUTION_FILTERS,
  DEFAULT_SALES_ATTRIBUTION_REVENUE_SOURCE,
  type SalesAttributionFilterState,
  type SalesAttributionRevenueSource,
} from "../utils/SalesAttributionTypes";
import { useSalesAttributionDashboard } from "../hooks/UseSalesAttributionDashboard";
import SalesAttributionChannelsSection from "./SalesAttributionChannelsSection";
import SalesAttributionFilterBar from "./SalesAttributionFilterBar";
import SalesAttributionKpiSection from "./SalesAttributionKpiSection";
import SalesAttributionOverlapSection from "./SalesAttributionOverlapSection";
import SalesAttributionReconciliationSection from "./SalesAttributionReconciliationSection";
import "./SalesProductAttributionSection.scss";

interface AttributionSectionProps {
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}

export const AttributionSection: FC<AttributionSectionProps> = ({
  dateRange,
  onDateRangeChange,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SalesAttributionFilterState>(
    DEFAULT_SALES_ATTRIBUTION_FILTERS
  );
  const [revenueSource, setRevenueSource] = useState<SalesAttributionRevenueSource>(
    DEFAULT_SALES_ATTRIBUTION_REVENUE_SOURCE
  );

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    const headerEl = sectionRef.current?.querySelector<HTMLElement>(".dashboard-section-header");
    if (!headerEl) {return;}
    const measure = () => setHeaderHeight(headerEl.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  const reportingMonth = useMemo(() => {
    const anchor = dayjs(dateRange.endDate || dateRange.startDate, "YYYY-MM-DD");
    return (anchor.isValid() ? anchor : dayjs()).startOf("month");
  }, [dateRange.startDate, dateRange.endDate]);

  const comparisonMonth = useMemo(
    () => reportingMonth.subtract(1, "month"),
    [reportingMonth],
  );

  const monthRange = useMemo(
    () => ({
      startDate: comparisonMonth.format("YYYY-MM-DD"),
      endDate: reportingMonth.endOf("month").format("YYYY-MM-DD"),
    }),
    [reportingMonth, comparisonMonth]
  );

  const handleMonthChange = ({ start, end }: { start: string; end: string }) => {
    const endMonth = dayjs(end, "YYYY-MM-DD");
    if (!endMonth.isValid()) {
      return;
    }
    const startMonth = dayjs(start, "YYYY-MM-DD");
    onDateRangeChange({
      startDate: (startMonth.isValid() ? startMonth : endMonth.subtract(1, "month"))
        .startOf("month")
        .format("YYYY-MM-DD"),
      endDate: endMonth.endOf("month").format("YYYY-MM-DD"),
    });
  };

  const { data, loading, scorecardLoading, error } = useSalesAttributionDashboard(
    monthRange,
    filters,
    revenueSource,
  );

  const narrativePayloadData = useMemo(
    () => (data ? (data as unknown as Record<string, unknown>) : {}),
    [data]
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "sales_product_analytics",
    tabKey: "attribution",
    startDate: monthRange.startDate,
    endDate: monthRange.endDate,
    data: narrativePayloadData,
    enabled: !loading && !error && Boolean(data),
  });

  const renderBody = () => {
    if (loading) {
      return (
        <div className="sales-attribution__loading">
          <LoadingSpinner compact />
        </div>
      );
    }
    if (error) {
      return <DashboardErrorState message={error} />;
    }
    if (!data) {
      return (
        <DashboardErrorState
          message={t("salesAttribution.ui.noData", {
            defaultValue: "No sales attribution data is available for the selected period.",
          })}
        />
      );
    }

    return (
      <>

        {data.warnings.length > 0 ? (
          <div className="sales-attribution__warnings" role="status">
            {data.warnings.map(warning => (
              <p key={warning} className="sales-attribution__warning">
                {warning}
              </p>
            ))}
          </div>
        ) : null}

        <SalesAttributionKpiSection kpis={data.kpis} />
        <SalesAttributionReconciliationSection trend={data.trend} platformFilter={filters.platform} />
        <SalesAttributionOverlapSection overlap={data.overlap} />
        <SalesAttributionChannelsSection
          channelComparison={data.channelComparison}
          channelRows={data.channelRows}
          scorecardTotal={data.scorecardTotal}
          scorecardLoading={scorecardLoading}
          revenueSource={revenueSource}
          onRevenueSourceChange={setRevenueSource}
        />
      </>
    );
  };

  return (
    <div
      ref={sectionRef}
      className="sales-product-analytics-dashboard__section sales-product-analytics-dashboard__section--attribution"
    >
      <DashboardSectionHeader
        title={t("salesAttribution.ui.title", { defaultValue: "Sales Attribution" })}
        subtitle={t("salesAttribution.ui.subtitle", {
          defaultValue:
            "Select the two months from the date pickers to compare your attribution performance",
        })}
        sticky
        actions={
          <div className="sales-attribution__header-controls">
            <div className="sales-attribution__date-row">
              <MonthRangePicker
                startDate={comparisonMonth.format("YYYY-MM-DD")}
                endDate={reportingMonth.format("YYYY-MM-DD")}
                onRangeChange={handleMonthChange}
                lockEndToNextMonth
              />
              <DashboardExportButton
                dashboardName="Sales-Attribution"
                dateRange={monthRange}
                containerRef={sectionRef}
                disabled={loading || Boolean(error) || !data}
              />
            </div>
          </div>
        }
      />
      <div
        className="sales-attribution__lens-dropdown-boundary"
        style={{ top: headerHeight }}
        aria-hidden
      />
      <div className="sales-attribution__main">
        {data ? (
          <SalesAttributionFilterBar
            filters={filters}
            options={data.filterOptions}
            onChange={setFilters}
          />
        ) : null}
        <div data-pdf-block="narrative">
          <DashboardNarrativePanel
            narrative={narrative}
            loading={loading || narrativeLoading}
            aiSummary={aiSummary}
          />
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default AttributionSection;
